<template>
  <div>
    <b-row>
      <b-col lg="6" offset-lg="3">
        <CCard>
          <CCardHeader>
            <h5>Define tu horario</h5>            
          </CCardHeader>
          <CCardBody>
            <!-- Indicador de pasos -->
            <b-row class="mb-4">
              <b-col cols="12">
                <div class="step-indicator">
                  <div class="step" :class="{ active: currentStep === 1, completed: currentStep > 1 }">
                    <div class="step-number">1</div>
                    <div class="step-label">Seleccionar fechas</div>
                  </div>
                  <div class="step-line" :class="{ completed: currentStep > 1 }"></div>
                  <div class="step" :class="{ active: currentStep === 2, completed: currentStep > 2 }">
                    <div class="step-number">2</div>
                    <div class="step-label">Seleccionar horarios</div>
                  </div>
                </div>
              </b-col>
            </b-row>

            <!-- Paso 1: Selección de fechas -->
            <div v-show="currentStep === 1">
              <b-row>
                <b-col lg="12" class="mb-3">
                  <h6 class="text-primary">Paso 1: Selecciona el rango de fechas</h6>
                  <p class="text-muted small">
                    <strong>Importante:</strong> Debes hacer dos clics: primero selecciona la fecha inicial, luego la fecha final.
                  </p>
              </b-col>
              <b-col lg="6">
                <b-form-group>
                  <v-date-picker
                    mode="range"
                    v-model="selectedDate"
                    color="blue"
                    is-inline
                    :min-date="minDate"
                      @input="onDateRangeSelected"
                  />
                </b-form-group>
              </b-col>
              <b-col lg="6">
                <b-row>
                    <b-col lg="12" class="mb-2">
                      <div class="date-display">
                        <strong>Fecha inicial:</strong>
                        <div class="date-value" :class="{ 'text-danger': !selectedDate.start }">
                          {{ formatDateDisplay(selectedDate.start) }}
                        </div>
                      </div>
                    </b-col>
                    <b-col lg="12" class="mb-2">
                      <div class="date-display">
                        <strong>Fecha final:</strong>
                        <div class="date-value" :class="{ 'text-danger': !selectedDate.end }">
                          {{ formatDateDisplay(selectedDate.end) }}
                        </div>
                      </div>
                    </b-col>
                    <b-col lg="12" v-if="selectedDate.start && selectedDate.end">
                      <b-button
                        class="schedule-flow-cta schedule-flow-cta--primary"
                        variant="primary"
                        @click="loadAssignedServices"
                        :disabled="loadingServices"
                        block>
                        <span v-if="loadingServices">Cargando servicios...</span>
                        <span v-else>Ver servicios asignados</span>
                      </b-button>
                    </b-col>
                  </b-row>
                  </b-col>
              </b-row>

              <!-- Servicios asignados -->
              <b-row v-if="servicesLoaded && !loadingServices && assignedServices.length > 0" class="mt-4">
                <b-col lg="12">
                  <b-alert variant="info" show>
                    <strong>Servicios asignados en este rango:</strong>
                    <div class="mt-2">
                      <div 
                        v-for="(service, index) in assignedServices" 
                        :key="index"
                        class="service-item mb-2 p-2 bg-light rounded">
                        <strong>{{ service.fecha }}</strong> - 
                        {{ service.hora_inicio }} a {{ service.hora_fin }} | 
                        Cliente: {{ service.cliente }} | 
                        Mascota: {{ service.mascota }}
                      </div>
                    </div>
                  </b-alert>
                </b-col>
              </b-row>
              <b-row v-if="servicesLoaded && !loadingServices && assignedServices.length === 0 && selectedDate.start && selectedDate.end" class="mt-4">
                <b-col lg="12">
                  <b-alert variant="success" show>
                    <strong>✓ No tienes servicios asignados en este rango de fechas.</strong>
                    <p class="mb-0 mt-1">Puedes bloquear cualquier horario sin conflictos.</p>
                  </b-alert>
                </b-col>
              </b-row>

              <b-row v-if="selectedDate.start && selectedDate.end" class="mt-3">
                  <b-col lg="12">
                    <b-alert v-if="!servicesLoaded" variant="warning" show class="mb-3">
                      <strong>⚠️ Importante:</strong> Primero debes hacer clic en "Ver servicios asignados" para conocer tus servicios antes de bloquear horarios.
                    </b-alert>
                    <b-button
                    class="schedule-flow-cta schedule-flow-cta--success"
                    variant="success"
                    @click="goToStep2"
                    :disabled="!servicesLoaded || loadingServices"
                    block>
                    <span v-if="loadingServices">
                      <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      Cargando servicios...
                    </span>
                    <span v-else-if="!servicesLoaded">Primero ver servicios asignados</span>
                    <span v-else>Continuar al paso 2</span>
                    </b-button>
                  </b-col>
                </b-row>
            </div>

            <!-- Paso 2: Selección de horarios -->
            <div v-show="currentStep === 2">
            <b-row>
                <b-col lg="12" class="mb-3">
                  <h6 class="text-primary">Paso 2: Indica las horas en que NO PRESTAS tu servicio</h6>
                  <p class="text-muted small">
                    Selecciona los días incluidos en el rango del paso 1 y define los rangos de horas a bloquear.
                    Puedes elegir la hora inicial y final de una vez (ej. 06:00 a 09:00), bloquear el día completo
                    con un clic, o copiar los horarios de un día a los demás días disponibles.
                  </p>
              </b-col>
              
              <b-col lg="12">
                <ScheduleDays 
                  :schedule="{}" 
                  :dates="dates" 
                  :times="times"
                    :assigned-services="assignedServices"
                    :selected-date-range="selectedDate"
                    @forbiddenDatesAndTimes="onForbiddenDatesAndTimesChanged" />
                </b-col>

                <!-- Alertas de conflictos -->
                <b-col lg="12" v-if="conflictMessages.length > 0" class="mt-3">
                  <b-alert 
                    v-for="(msg, index) in conflictMessages" 
                    :key="index"
                    variant="warning" 
                    dismissible
                    show>
                    <strong>⚠️ Conflicto detectado:</strong> {{ msg }}
                  </b-alert>
                </b-col>
              </b-row>

              <div class="schedule-step-actions mt-4">
                <b-button
                  class="schedule-flow-cta schedule-flow-cta--info schedule-step-actions__primary"
                  variant="info"
                  @click="getSchedule"
                  :disabled="loadingGetSchedule"
                  block>
                  <span v-if="loadingGetSchedule">
                    <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    Cargando...
                  </span>
                  <span v-else>Ver horarios bloqueados</span>
                </b-button>

                <b-button
                  class="schedule-step-actions__secondary"
                  variant="outline-secondary"
                  @click="goToStep1"
                  block>
                  ← Volver al paso 1
                </b-button>
              </div>
            </div>

            <Modal 
              :show="showScheduleModal"
              :schedule="schedule"/>
              
            <!-- Botón para consultar horarios bloqueados desde el paso 1 -->
            <b-row v-if="currentStep === 1 && selectedDate.start && selectedDate.end" class="mt-3">
              <b-col lg="12">
                <b-button
                  class="schedule-flow-cta schedule-flow-cta--info"
                  variant="info"
                  @click="getSchedule"
                  :disabled="loadingGetSchedule"
                  block>
                  <span v-if="loadingGetSchedule">
                    <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    Cargando...
                  </span>
                  <span v-else>Ver horarios bloqueados</span>
                </b-button>
              </b-col>
            </b-row>
          </CCardBody>
          <CCardFooter v-if="currentStep === 2" class="schedule-footer">
            <div class="schedule-footer__primary">
              <b-button
                class="schedule-footer__save"
                @click="saveSchedule"
                variant="success"
                size="lg"
                :disabled="hasConflicts || !canSave || loadingSaveSchedule">
                <span v-if="loadingSaveSchedule">
                  <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  Guardando...
                </span>
                <span v-else>✓ Guardar horario</span>
              </b-button>
            </div>

            <div class="schedule-footer__secondary-label">
              Acciones adicionales
            </div>

            <div class="schedule-footer__secondary">
              <b-button
                class="schedule-footer__reset"
                @click="resetToInitialState"
                variant="outline-secondary">
                Nuevo bloqueo
              </b-button>

              <b-button
                class="schedule-footer__delete"
                @click="deleteSchedule"
                variant="outline-danger">
                Eliminar horarios bloqueados
              </b-button>
            </div>
          </CCardFooter>
        </CCard>
      </b-col>
      <ModalMsg
       :msgs="msgs"
       :show="showScheduleMsgModal"
       @update:show="showScheduleMsgModal = $event"/>
    </b-row>
  </div>
</template>

<script>
import { DatePicker } from "v-calendar";
import moment from "moment";
moment.locale("es");

import times from "../../../utils/times";
import dates from "../../../utils/dates";
import ScheduleDays from "../../../components/ScheduleDays";
import Modal from "./Modal";
import ModalMsg from "./ModalManageSchedule"

const today = new Date();
const createEmptyDatesModel = () =>
  JSON.parse(JSON.stringify(dates)).map(date => ({
    ...date,
    active: false,
    times: []
  }));
export default {
  name: "providerSchedule",
  components: {
    "v-date-picker": DatePicker,
    ScheduleDays,
    Modal,
    ModalMsg,
  },
  data() {
    return {
      currentStep: 1,
      selectedDate: {
        start: null,
        end: null
      },
      dates: createEmptyDatesModel(),
      times: times,
      forbiddenDatesAndTimes: null,
      userProfile: null,
      schedule: null,
      showScheduleModal: false,
      showScheduleMsgModal: false,
      minDate: new Date(),
      maxDate: new Date(),
      msgs: [],
      assignedServices: [],
      loadingServices: false,
      conflictMessages: [],
      loadingSaveSchedule: false,
      loadingGetSchedule: false,
      servicesLoaded: false // Bandera para saber si ya se intentó cargar servicios
    };
  },

  created(){
    this.userProfile = JSON.parse(this.$store.getters.userProfile);
  
    this.watchs()
    this.setMaxDate()
  },
  computed: {
    hasConflicts() {
      return this.conflictMessages.length > 0;
    },
    canSave() {
      if (!this.forbiddenDatesAndTimes) return false;
      let filtered = this.forbiddenDatesAndTimes.filter(
        forbiddenItem => forbiddenItem.active == true 
        && forbiddenItem.times.length > 0
      );
      return filtered.length > 0 && this.validate(filtered) && this.validateSelectedDaysWithinRange(filtered);
    }
    },
  methods: {
    formatDateDisplay(date) {
      if (!date) {
        return 'No seleccionada';
      }
      // Asegurarse de que sea un objeto Date válido
      const dateObj = date instanceof Date ? date : new Date(date);
      if (isNaN(dateObj.getTime())) {
        return 'Fecha inválida';
      }
      return moment(dateObj).format('ddd, DD-MMM-YYYY');
    },
    onDateRangeSelected() {
      // Reset servicios y bloqueos cuando cambia el rango de fechas
      if (this.selectedDate.start && this.selectedDate.end) {
        this.assignedServices = [];
        this.servicesLoaded = false;
        this.forbiddenDatesAndTimes = null;
        this.conflictMessages = [];
        this.dates = createEmptyDatesModel();
      }
    },
    async loadAssignedServices() {
      if (!this.selectedDate.start || !this.selectedDate.end) {
        return;
      }

      this.loadingServices = true;
      this.assignedServices = [];
      this.servicesLoaded = false; // Resetear antes de cargar

      try {
        let data = {
          email: this.userProfile.email,
          dateStart: moment(this.selectedDate.start).format('YYYY-MM-DD') + "T00:00:00.000Z",
          dateFinish: moment(this.selectedDate.end).format('YYYY-MM-DD') + "T23:59:59.000Z"
        };

        let getServices = this.$firebase.functions().httpsCallable('getWalkerServices');
        const { data: response } = await getServices(data);
        
        if (response && response.items) {
          this.assignedServices = response.items;
        }
        this.servicesLoaded = true; // Marcar como cargado después de la consulta
      } catch (error) {
        console.error('Error cargando servicios:', error);
        window.alert('Error al cargar los servicios asignados');
        this.servicesLoaded = false;
      } finally {
        this.loadingServices = false;
      }
    },
    goToStep1() {
      this.currentStep = 1;
    },
    async goToStep2() {
      if (!this.selectedDate.start || !this.selectedDate.end) {
        window.alert('Por favor selecciona ambas fechas (inicial y final)');
        return;
      }
      
      // Si no se han cargado los servicios, cargarlos primero
      if (!this.servicesLoaded && !this.loadingServices) {
        await this.loadAssignedServices();
      }
      
      // Solo continuar si los servicios se cargaron correctamente
      if (this.servicesLoaded) {
        this.currentStep = 2;
      } else {
        window.alert('Debes cargar los servicios asignados antes de continuar. Por favor haz clic en "Ver servicios asignados".');
      }
    },
    onForbiddenDatesAndTimesChanged(dates) {
      this.forbiddenDatesAndTimes = dates;
      this.validateConflicts();
    },
    normalizeDayName(day) {
      const normalizedDay = (day || "").toLowerCase().trim();
      const dayMap = {
        'dom': 'dom.',
        'dom.': 'dom.',
        'lun': 'lun.',
        'lun.': 'lun.',
        'mar': 'mar.',
        'mar.': 'mar.',
        'mie': 'mié.',
        'mie.': 'mié.',
        'mié': 'mié.',
        'mié.': 'mié.',
        'jue': 'jue.',
        'jue.': 'jue.',
        'vie': 'vie.',
        'vie.': 'vie.',
        'sab': 'sáb.',
        'sab.': 'sáb.',
        'sáb': 'sáb.',
        'sáb.': 'sáb.'
      };

      return dayMap[normalizedDay] || normalizedDay;
    },
    getAllowedDayKeys() {
      if (!this.selectedDate.start || !this.selectedDate.end) {
        return [];
      }

      const allowedDays = new Set();
      let currentDate = moment(this.selectedDate.start).startOf('day');
      const endDate = moment(this.selectedDate.end).startOf('day');
      const dayKeys = ['dom.', 'lun.', 'mar.', 'mié.', 'jue.', 'vie.', 'sáb.'];

      while (currentDate.isSameOrBefore(endDate)) {
        allowedDays.add(dayKeys[currentDate.day()]);
        currentDate = currentDate.clone().add(1, 'day');
      }

      return Array.from(allowedDays);
    },
    getOutOfRangeForbiddenDays(forbiddenDatesAndTimes = this.forbiddenDatesAndTimes) {
      if (!Array.isArray(forbiddenDatesAndTimes)) {
        return [];
      }

      const allowedDayKeys = this.getAllowedDayKeys();

      return forbiddenDatesAndTimes.filter(daySchedule =>
        daySchedule.active &&
        daySchedule.times.length > 0 &&
        !allowedDayKeys.includes(this.normalizeDayName(daySchedule.day))
      );
    },
    validateSelectedDaysWithinRange(forbiddenDatesAndTimes = this.forbiddenDatesAndTimes, showAlert = false) {
      const invalidDays = this.getOutOfRangeForbiddenDays(forbiddenDatesAndTimes);

      if (invalidDays.length === 0) {
        return true;
      }

      if (showAlert) {
        const dayNames = invalidDays.map(item => item.day).join(', ');
        window.alert(`Solo puedes bloquear horarios para los dias incluidos en el rango consultado en el paso 1. Ajusta estos dias: ${dayNames}.`);
      }

      return false;
    },
    validateConflicts() {
      this.conflictMessages = [];
      
      if (!this.forbiddenDatesAndTimes || !this.selectedDate.start || !this.selectedDate.end) {
        return;
      }

      if (this.assignedServices.length === 0) {
        return;
      }

      // Obtener todos los días del rango seleccionado
      const startDate = moment(this.selectedDate.start);
      const endDate = moment(this.selectedDate.end);
      const daysInRange = [];
      let currentDate = startDate.clone();
      
      while (currentDate.isSameOrBefore(endDate)) {
        daysInRange.push(currentDate.clone());
        currentDate.add(1, 'day');
      }

      // Mapeo de días de la semana: moment.js usa 0=domingo, 1=lunes, etc.
      const dayNamesMap = {
        'dom': 0, 'dom.': 0,
        'lun': 1, 'lun.': 1,
        'mar': 2, 'mar.': 2,
        'mié': 3, 'mié.': 3, 'mie': 3, 'mie.': 3,
        'jue': 4, 'jue.': 4,
        'vie': 5, 'vie.': 5,
        'sáb': 6, 'sáb.': 6, 'sab': 6, 'sab.': 6
      };

      // Validar cada día activo en forbiddenDatesAndTimes
      this.forbiddenDatesAndTimes.forEach((daySchedule, dayIndex) => {
        if (!daySchedule.active || daySchedule.times.length === 0) {
          return;
        }

        // Obtener el número del día de la semana
        const dayName = daySchedule.day.toLowerCase().trim();
        const dayOfWeek = dayNamesMap[dayName];
        
        if (dayOfWeek === undefined) {
          console.warn('Día de la semana no reconocido:', daySchedule.day);
          return;
        }

        // Validar cada rango de horas bloqueado
        daySchedule.times.forEach((timeRange) => {
          if (!timeRange.start || !timeRange.end) {
            return;
          }

          // Convertir hora bloqueada a formato comparable
          const blockedStart = this.timeToMinutes(timeRange.start.label);
          const blockedEnd = this.timeToMinutes(timeRange.end.label);

          if (blockedStart === 0 && blockedEnd === 0) {
            return; // Horario inválido
          }

          // Buscar servicios asignados que coincidan con este día de la semana
          daysInRange.forEach((date) => {
            // Verificar si el día de la semana coincide
            if (date.day() !== dayOfWeek) {
              return;
            }

            // Validar contra cada servicio asignado
            this.assignedServices.forEach((service) => {
              // Intentar parsear la fecha del servicio
              const serviceDate = this.parseServiceDate(service.fecha, date);
              
              // Si no se puede parsear por fecha exacta, verificar por día de la semana
              if (!serviceDate || !serviceDate.isSame(date, 'day')) {
                // Si el servicio tiene un campo "dia" que coincida, también validar
                if (service.dia) {
                  const serviceDayName = service.dia.toLowerCase().trim();
                  const serviceDayOfWeek = dayNamesMap[serviceDayName];
                  if (serviceDayOfWeek !== dayOfWeek) {
                    return;
                  }
                } else {
                  return;
                }
              }

              const serviceStart = this.timeToMinutes(service.hora_inicio);
              const serviceEnd = this.timeToMinutes(service.hora_fin);

              if (serviceStart === 0 && serviceEnd === 0) {
                return; // Horario de servicio inválido
              }

              // Verificar si hay solapamiento
              if (this.hasTimeOverlap(blockedStart, blockedEnd, serviceStart, serviceEnd)) {
                const dateFormatted = date.format('DD/MM/YYYY');
                const conflictMsg = `El ${daySchedule.day} ${dateFormatted} tienes un servicio de ${service.hora_inicio} a ${service.hora_fin} (${service.mascota || 'sin mascota'}) que coincide con el horario que intentas bloquear (${timeRange.start.label} - ${timeRange.end.label})`;
                if (!this.conflictMessages.includes(conflictMsg)) {
                  this.conflictMessages.push(conflictMsg);
                }
              }
            });
          });
        });
      });
    },
    timeToMinutes(timeStr) {
      // Convierte "02:00 PM" o "14:00" a minutos desde medianoche
      if (!timeStr) return 0;
      
      // Si ya está en formato 24h (HH:mm)
      if (timeStr.match(/^\d{2}:\d{2}$/)) {
        const [hours, minutes] = timeStr.split(':').map(Number);
        return hours * 60 + minutes;
      }

      // Si está en formato 12h (HH:MM AM/PM)
      const match = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
      if (!match) return 0;

      let hours = parseInt(match[1]);
      const minutes = parseInt(match[2]);
      const period = match[3].toUpperCase();

      if (period === 'PM' && hours !== 12) {
        hours += 12;
      } else if (period === 'AM' && hours === 12) {
        hours = 0;
      }

      return hours * 60 + minutes;
    },
    parseServiceDate(dateStr, referenceDate) {
      // Parsear fecha en formato "30/11" o "30/11/2024" o similar
      if (!dateStr) return null;
      
      const parts = dateStr.split('/');
      if (parts.length < 2) return null;

      const day = parseInt(parts[0]);
      const month = parseInt(parts[1]) - 1; // Los meses en JS son 0-indexed
      
      if (isNaN(day) || isNaN(month) || month < 0 || month > 11) {
        return null;
      }

      // Si tiene año, usarlo; si no, usar el año del referenceDate
      let year = referenceDate.year();
      if (parts.length === 3) {
        year = parseInt(parts[2]);
        if (isNaN(year)) {
          year = referenceDate.year();
        }
      }

      const parsedDate = moment(new Date(year, month, day));
      
      // Validar que la fecha sea válida
      if (!parsedDate.isValid()) {
        return null;
      }

      return parsedDate;
    },
    hasTimeOverlap(start1, end1, start2, end2) {
      // Verifica si dos rangos de tiempo se solapan
      return start1 < end2 && start2 < end1;
    },
    saveSchedule(){
      if (this.hasConflicts) {
        window.alert('No puedes guardar el horario porque hay conflictos con servicios ya asignados. Por favor revisa las alertas.');
        return;
      }

      if (!this.servicesLoaded) {
        window.alert('Debes consultar los servicios asignados del rango seleccionado antes de guardar.');
        return;
      }

      if(this.forbiddenDatesAndTimes == null){
        window.alert('Error: No tienes horarios para asignar')
        return;
      }
      let forbiddenDatesAndTimesFiltered = this.forbiddenDatesAndTimes
      .filter(
        forbiddenItem => forbiddenItem.active == true 
        && forbiddenItem.times.length > 0
      )
      if(forbiddenDatesAndTimesFiltered.length == 0){
        window.alert('Error: No hay horarios para almacenar')
        return;
      }
      if(!this.validateSelectedDaysWithinRange(forbiddenDatesAndTimesFiltered, true)){
        return;
      }
      let isValid = this.validate(forbiddenDatesAndTimesFiltered) 
      if(!isValid){
        window.alert('Error: No pueden haber campos de horarios sin gestionar')
        return;
      }
      
      this.loadingSaveSchedule = true;
      let payload = {
        selectedDate: {
          start: moment(this.selectedDate.start).format('YYYY-MM-DD'),
          end: moment(this.selectedDate.end).format('YYYY-MM-DD')
        },
        forbiddenDatesAndTimes: forbiddenDatesAndTimesFiltered,
        userId: this.userProfile.usuario.id
      }

      let createWalkerSchedule = this.$firebase
        .functions()
        .httpsCallable('createWalkerScheduleV2')

      createWalkerSchedule(payload).then(res => {
        let data = res.data.data
        // console.log(data)        
        this.msgs = []
        data.map((item, i) => {
          let msgItem = null
          if('fecha' in item && 'horas' in item){
            msgItem = {
              date: moment(item.fecha).format('YYYY-MM-DD'),
              hours: item.horas,
              description: ''
            }
          
          } else if (item.status){
            let dateSend = moment(this.selectedDate.start)
              .add(i, 'days')
              .format('YYYY-MM-DD')
            msgItem = {
              date: dateSend,
              hours: null,
              description: item.description

            }            
          } else {
            msgItem = {
              date: null,
              hours: null,
              description: 'Error'
            }
          }
          this.msgs.push(msgItem)
        })   
        this.loadingSaveSchedule = false;
        
        // Mostrar el modal de confirmación usando el store
        this.$store.commit("defShowScheduleMsg", true);
        this.showScheduleMsgModal = true;
        
        // Cargar y mostrar automáticamente los horarios bloqueados para confirmación
        // Esto le da al usuario seguridad de que se guardó correctamente
        this.loadAndShowBlockedSchedule();
        
        // Resetear solo los horarios seleccionados después de mostrar los modales
        // Usar setTimeout para asegurar que los modales se muestren primero
        setTimeout(() => {
          this.resetScheduleData();
        }, 200);
        
      }).catch(error => {
        alert(error)
        this.loadingSaveSchedule = false;
      })
    },
    validate(dt){
      let isValid = true

      let size = dt.length
      for(let i=0; i < size; i++){
        let times = dt[i].times
        let flag = 
        times.some(item => 
          (item.start == null || item.end == null) 
        )
        if(flag) {
          isValid = false;
          break;
        }
      }
      return isValid
    },
    saveSchedule2(){
      let forbiddenDatesAndTimesFiltered = this.forbiddenDatesAndTimes
      .filter(
        forbiddenItem => forbiddenItem.active == true 
        && forbiddenItem.times.length > 0
      )

      let payload = {
        selectedDate: {
          start: moment(this.selectedDate.start).format('YYYY-MM-DD'),
          end: moment(this.selectedDate.end).format('YYYY-MM-DD')
        },
        forbiddenDatesAndTimes: forbiddenDatesAndTimesFiltered,
        userId: this.userProfile.usuario.id
      }

      let createWalkerSchedule = this.$firebase
        .functions()
        .httpsCallable('createWalkerScheduleV2')

      createWalkerSchedule(payload).then(res => {
        let data = res.data.data
        console.log(data)        
        this.msgs = []
        data.map((item, i) => {
          let msgItem = null
          if('fecha' in item && 'horas' in item){
            msgItem = {
              date: moment(item.fecha).format('YYYY-MM-DD'),
              hours: item.horas,
              description: ''
            }
           
          } else if (item.status){
            let dateSend = moment(this.selectedDate.start)
              .add(i, 'days')
              .format('YYYY-MM-DD')
            msgItem = {
              date: dateSend,
              hours: null,
              description: item.description

            }            
          } else {
            msgItem = {
              date: null,
              hours: null,
              description: 'Error'
            }
          }
           this.msgs.push(msgItem)
        })   
        this.showScheduleMsgModal = true
        
      }).catch(error => {
        alert(error)        
      })

    },
    loadAndShowBlockedSchedule() {
      // Método auxiliar para cargar y mostrar horarios bloqueados
      if (!this.selectedDate.start || !this.selectedDate.end) {
        return;
      }
      
      let payload = {
        userId: this.userProfile.usuario.id,
        selectedDate: {
          start: moment(this.selectedDate.start)
            .format('YYYY-MM-DD') 
            + 'T00:00:00.000Z',
          end: moment(this.selectedDate.end)
            .format('YYYY-MM-DD')
            + 'T00:00:00.000Z'
        }
      }
      let getWalkerSchedule = this.$firebase
        .functions()
        .httpsCallable('getWalkerScheduleV2')

      getWalkerSchedule(payload).then(res => {
        let data = res.data.data
        if (data && data.length > 0) {
          data.map(item => {
            item.horas = JSON.parse(item.horas)
          })
          this.schedule = data
          // Mostrar el modal después de un pequeño delay para que el usuario vea primero el modal de confirmación
          setTimeout(() => {
            this.$store.commit("defShowProviderScheduleModal", true);
            this.showScheduleModal = true;
          }, 500);
        }
      }).catch(error => {
        console.error('Error cargando horarios bloqueados:', error);
        // No mostrar error al usuario, es solo para confirmación
      })
    },
    getSchedule(){
      this.loadingGetSchedule = true;
      let payload = {
        userId: this.userProfile.usuario.id,
        selectedDate: {
          start: moment(this.selectedDate.start)
            .format('YYYY-MM-DD') 
            + 'T00:00:00.000Z',
          end: moment(this.selectedDate.end)
            .format('YYYY-MM-DD')
            + 'T00:00:00.000Z'
        }
      }
      let getWalkerSchedule = this.$firebase
        .functions()
        .httpsCallable('getWalkerScheduleV2')

      getWalkerSchedule(payload).then(res => {
        let data = res.data.data
        data.map(item => {
          item.horas = JSON.parse(item.horas)
        })
        this.schedule = data
        this.showScheduleModal = true
        this.$store.commit("defShowProviderScheduleModal", true);
        this.loadingGetSchedule = false;
      }).catch(error => {
        alert(error)
        this.loadingGetSchedule = false;
      })
    },
    deleteSchedule(){
      let payload = {
        userId: this.userProfile.usuario.id,
        selectedDate: {
          start: moment(this.selectedDate.start)
            .format('YYYY-MM-DD') 
            + 'T00:00:00.000Z',
          end: moment(this.selectedDate.end)
            .format('YYYY-MM-DD')
            + 'T00:00:00.000Z'
        }
      }

      let deleteSchedules = this.$firebase
        .functions()
        .httpsCallable('deleteWalkerSchedulesV2')

      deleteSchedules(payload).then(response => {
        let data = response.data.data
        // console.log(data)
        window.alert('Limpieza exitosa. Los horarios han sido borrados')
      }).catch(error => {
        console.log(error)
        window.alert('No se pudo eliminar los horarios')
      })
    },
    watchs(){
      this.$store.subscribe((mutation, state) => {
        if (mutation.type === "defShowProviderScheduleModal") {
          this.showScheduleModal = this.$store.getters.showProviderScheduleModal
        }
        if (mutation.type === "defShowScheduleMsg") {
          this.showScheduleMsgModal = this.$store.getters.showScheduleMsg
          // Si el modal se cierra, asegurarse de que el estado local esté sincronizado
          if (!this.$store.getters.showScheduleMsg) {
            this.showScheduleMsgModal = false;
          }
        }
      });
    },
    setMaxDate(){
      // Usar fecha local al inicio del dia para evitar desfases por zona horaria
      this.minDate = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
        0,
        0,
        0,
        0
      );
    },
    resetScheduleData() {
      // Mantener al usuario en el paso 2 para que pueda consultar horarios bloqueados
      // No resetear currentStep, mantener en paso 2
      
      // NO limpiar fechas seleccionadas - mantenerlas para consultar
      // this.selectedDate se mantiene
      
      // Limpiar horarios bloqueados seleccionados
      this.forbiddenDatesAndTimes = null;
      
      // Resetear días de la semana - clonar el array original
      this.dates = createEmptyDatesModel();
      
      // NO limpiar servicios asignados - mantenerlos para referencia
      // this.assignedServices se mantiene
      // this.servicesLoaded se mantiene
      
      // Limpiar mensajes de conflicto
      this.conflictMessages = [];
    },
    resetToInitialState() {
      // Método para limpiar TODO cuando el usuario quiera empezar de nuevo
      // Resetear al paso 1
      this.currentStep = 1;
      
      // Limpiar fechas seleccionadas
      this.selectedDate = {
        start: null,
        end: null
      };
      
      // Limpiar horarios bloqueados
      this.forbiddenDatesAndTimes = null;
      
      // Resetear días de la semana - clonar el array original
      this.dates = createEmptyDatesModel();
      
      // Limpiar servicios asignados
      this.assignedServices = [];
      this.servicesLoaded = false;
      
      // Limpiar mensajes de conflicto
      this.conflictMessages = [];
    },
  }
};
</script>

<style lang="scss" scoped>
.step-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 20px 0;
  
  .step {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    
    .step-number {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background-color: #e0e0e0;
      color: #666;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: bold;
      margin-bottom: 8px;
      transition: all 0.3s ease;
    }
    
    .step-label {
      font-size: 12px;
      color: #666;
      text-align: center;
      white-space: nowrap;
    }
    
    &.active {
      .step-number {
        background-color: #007bff;
        color: white;
      }
      .step-label {
        color: #007bff;
        font-weight: bold;
      }
    }
    
    &.completed {
      .step-number {
        background-color: #28a745;
        color: white;
      }
      .step-label {
        color: #28a745;
      }
    }
  }
  
  .step-line {
    width: 60px;
    height: 2px;
    background-color: #e0e0e0;
    margin: 0 10px;
    margin-bottom: 20px;
    transition: all 0.3s ease;
    
    &.completed {
      background-color: #28a745;
    }
  }
}

.date-display {
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 4px;
  margin-bottom: 10px;
  
  .date-value {
    font-size: 16px;
    margin-top: 5px;
  }
}

.service-item {
  font-size: 14px;
  border-left: 3px solid #007bff;
}

.schedule-flow-cta {
  min-height: 52px;
  border-radius: 14px;
  font-weight: 700;
  letter-spacing: 0.01em;
  transition: transform 0.18s ease, box-shadow 0.18s ease, filter 0.18s ease;
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.12);

  &:hover:not(:disabled),
  &:focus:not(:disabled) {
    transform: translateY(-1px);
    filter: saturate(1.03);
  }

  &:disabled {
    box-shadow: none;
  }

  &--success {
    background: linear-gradient(135deg, #28a745 0%, #218838 100%);
    border-color: #218838;
  }

  &--info {
    background: linear-gradient(135deg, #17a2b8 0%, #138496 100%);
    border-color: #138496;
  }

  &--primary {
    background: linear-gradient(135deg, #0d6efd 0%, #0b5ed7 100%);
    border-color: #0b5ed7;
  }
}

.schedule-step-actions {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
  gap: 12px;
  align-items: stretch;

  &__primary,
  &__secondary {
    min-height: 48px;
    border-radius: 12px;
    font-weight: 600;
    box-shadow: none;
  }

  &__primary {
    box-shadow: 0 12px 24px rgba(23, 162, 184, 0.22);
  }

  &__secondary {
    background-color: #fff;
  }
}

.schedule-footer {
  padding-top: 1.25rem;
  border-top: 1px solid rgba(15, 23, 42, 0.08);
  background: linear-gradient(180deg, rgba(248, 250, 252, 0.95) 0%, #ffffff 100%);

  &__primary {
    margin-bottom: 1rem;
  }

  &__save {
    width: 100%;
    min-height: 56px;
    border-radius: 14px;
    font-weight: 700;
    letter-spacing: 0.01em;
    box-shadow: 0 12px 24px rgba(40, 167, 69, 0.18);
  }

  &__secondary-label {
    margin-bottom: 0.75rem;
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #6c757d;
  }

  &__secondary {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }

  &__reset,
  &__delete {
    width: 100%;
    min-height: 46px;
    border-radius: 12px;
    font-weight: 600;
  }
}

@media (max-width: 768px) {
  .step-indicator {
    .step {
      .step-label {
        font-size: 10px;
      }
    }
    
    .step-line {
      width: 30px;
    }
  }

  .schedule-step-actions,
  .schedule-footer__secondary {
    grid-template-columns: 1fr;
  }
}
</style>
